import { CreateFloorRule, UpdateFloorRule } from '@pubstack/common/src/adm/rules'
import { useNavigate, useParams } from 'react-router-dom'
import { useScopePermissions } from '~/auth/useScopePermissions'
import { useCreateFloorRule, useFloorRule, useFloorRules, useUpdateFloorRule } from '~/api/app/floor-rules.api'
import { Link } from '~/components/Link'
import { useToast } from '~/components/Toast/useToasts'
import { useBasePath } from '~/hooks/useBasePath'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { useScope } from '~/hooks/useScope'
import { scopeOwnsOffer } from '~/modules/monetization/upgrade/scopeOffers'
import { useScopeCurrency } from '~/hooks/useScopeCurrency.hooks'
import { PureFloorPriceEditPage } from './PureFloorPriceEditPage'

export const FloorPriceEditPage: React.FunctionComponent = () => {
  const navigate = useNavigate()
  const toast = useToast()
  const breadcrumbs = useBreadcrumbs()
  const currencySymbol = useScopeCurrency()
  const basePath = useBasePath()
  const { floorRuleId = '' } = useParams()
  const scope = useScope()
  const { canEdit, disabledReason } = useScopePermissions()
  // Sizes and dynamic GAM UPR are One's; a Prebid Server rule cannot express either. The page used to
  // read that off the URL it was mounted on, which cannot work now there is one URL — and never really
  // worked for a scope holding both, where the very same rule offered different fields per menu entry.
  // Asked of the scope instead: One anywhere means One's stacks can use the richer form.
  const hasOne = scopeOwnsOffer(scope, 'one')

  const { data: floorRule, isFetching, isError } = useFloorRule(floorRuleId)
  const { data: floorRules } = useFloorRules()

  const { mutateAsync: createFloorRule } = useCreateFloorRule({ errorToast: 'An error occurred while saving this rule.' })
  const { mutateAsync: updateFloorRule } = useUpdateFloorRule({ errorToast: 'An error occurred while saving this rule.' })

  // The mutations already toast the error through `errorToast`, so swallow the rejection to avoid an unhandled one
  const onCreateFloorRule = async (floorRule: CreateFloorRule) => {
    try {
      const newRule = await createFloorRule(floorRule)
      toast.success(`${newRule.name} successfully created.`)
      navigate(`${basePath?.replace('/new', '')}`)
    } catch (_e) {
      // already toasted
    }
  }

  const onUpdateFloorRule = async (floorRule: UpdateFloorRule, stacksUse: boolean) => {
    try {
      const updatedFloorRule = await updateFloorRule(floorRule)
      toast.success(
        <>
          <strong>{floorRule.name ?? ''}</strong> successfully updated !
          {stacksUse && floorRule.id !== updatedFloorRule.id && (
            <div>
              Some stacks are impacted by this change. They need to be updated and deployed again. Find them in the “out-of-sync” section of the{' '}
              <Link onClick={() => navigate && navigate('/monetization/inventory/deployment-center')} label={'deployment center'} trackingLabel={'common-deployment-center-navigate'} />.
            </div>
          )}
        </>,
        {
          duration: 10000,
        }
      )
      navigate(`${basePath?.replace('/edit', '')}`)
    } catch (_e) {
      // already toasted
    }
  }

  return (
    <PureFloorPriceEditPage
      isEditing={!!floorRuleId}
      isLoading={isFetching}
      isError={isError}
      floorRule={floorRule}
      existingFloorRules={floorRules}
      currencySymbol={currencySymbol}
      breadcrumbs={breadcrumbs}
      onCreateFloorRule={onCreateFloorRule}
      onUpdateFloorRule={onUpdateFloorRule}
      onBack={() => navigate(-1)}
      isPbs={!hasOne}
      canSave={canEdit}
      saveDisabledReason={disabledReason}
    />
  )
}
